import React from "react";
import {
  Factory,
  CheckCircle2,
  Wrench,
  ClipboardList,
  Settings,
  ShieldCheck,
  TrendingUp,
  Users,
} from "lucide-react";
import HeaderMenu from "../../component/Navbar/headermenu";
import CustomFooter from "../../component/Footer/customFooter";
import ShowcaseImageLightbox from "../../component/ShowcaseImageLightbox";
import "./about.css";
import taskTable from "../../assets/manufact/mf-tasks.png";
import peekView from "../../assets/manufact/mf-peekview.png";
import calendarView from "../../assets/manufact/mf-calendar.png";
import notes from "../../assets/manufact/mf-notes.png";

const features = [
  {
    icon: <Wrench size={26} />,
    title: "Shop Floor Tracking",
    description:
      "Assign tasks to specific production lines, machines, or shifts, tracking them from raw materials to QA.",
  },
  {
    icon: <ClipboardList size={26} />,
    title: "Inventory Checksheets",
    description:
      "Track stock levels, material audits, and procurement requests with detailed forms and deadlines.",
  },
  {
    icon: <Settings size={26} />,
    title: "Equipment Maintenance",
    description:
      "Schedule preventative maintenance, machine downtime reviews, and utility checkups to avoid sudden failures.",
  },
  {
    icon: <ShieldCheck size={26} />,
    title: "Quality Assurance",
    description:
      "Standardize QA inspections, defect reporting, and compliance sign-offs with direct supervisor approval.",
  },
];

const ManufacturingPage = () => {
  const showcase = [
    {
      eyebrow: "Every Line, One Task Board",
      title: "Production, warehouse, and quality — tracked in one place",
      description:
        "Dispatch & Logistics, Warehouse, Procurement, Inventory Management, Maintenance, Quality Control, and Production — every task for Larsen Engineering, Adani Manufacturing, Tata Manufacturing, Mahindra Components, and Reliance Industries tracked with client, status, period, and financial year in one board.",
      image: taskTable,
      alt: "Simplifyte task table showing manufacturing tasks across Dispatch and Logistics, Warehouse, Inventory Management, Maintenance, and Quality Control with client and status columns",
    },
    {
      eyebrow: "Every Task, With Full Context",
      title: "Open any task without losing the bigger picture",
      description:
        "A task like 'Review low-stock material alerts' opens in a side peek panel with its client (Larsen Engineering), financial year, period, due date, and assignee — full context, without leaving the task list behind.",
      image: peekView,
      alt: "Simplifyte task peek panel showing Review low-stock material alerts for Larsen Engineering with financial year, due date, and assignee",
    },
    {
      eyebrow: "Calendar View",
      title: "See overdue and upcoming tasks across every client, by day",
      description:
        "A calendar view groups tasks by due date across Reliance Industries, Mahindra Components, Larsen Engineering, and Tata Manufacturing — so plant managers can see exactly which days are overloaded before they happen, not after.",
      image: calendarView,
      alt: "Simplifyte calendar view showing manufacturing tasks due across different days for Reliance Industries, Mahindra Components, Larsen Engineering, and Tata Manufacturing",
    },
    {
      eyebrow: "Shift Notes",
      title: "Daily operations notes, saved where the team can find them",
      description:
        "Today's priorities and follow-ups — production targets, raw material checks, machine downtime, quality inspections, maintenance schedules, and shift handover reports — written up as a note attached to the workspace, not lost in a WhatsApp thread.",
      image: notes,
      alt: "Simplifyte private note titled Production Line Daily Operations Notes listing today's priorities and follow-ups",
    },
  ];

  const benefits = [
    "Track every production, warehouse, and quality task on one board",
    "See client, period, and assignee context without status meetings",
    "Catch overdue and at-risk tasks with a calendar view of every due date",
    "Keep shift handover and daily operations notes in one place",
    "Give plant managers full visibility across every production line",
  ];

  return (
    <>
      <HeaderMenu />
      <div className="Industry_class_main">
        {/* Hero */}
        <section className="Industry_class_hero">
          <div className="Industry_class_container">
            <div className="Industry_class_hero_content">
              <div className="Industry_class_icon_box">
                <Factory size={34} />
              </div>

              <h1 className="Industry_class_heading">
                Production and inventory management software for
                Manufacturing teams
              </h1>

              <p className="Industry_class_subheading">
                Track production, warehouse, and quality tasks across every
                client and line, with calendar views and shift notes that
                keep plant managers ahead of delays.
              </p>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="Industry_class_features_section">
          <div className="Industry_class_container">
            <h2 className="Industry_class_section_title Industry_class_center">
              Everything manufacturing teams need in one platform
            </h2>

            <div className="Industry_class_features_grid">
              {features.map((feature, index) => (
                <div key={index} className="Industry_class_feature_card">
                  <div className="Industry_class_feature_icon">
                    {feature.icon}
                  </div>

                  <h3 className="Industry_class_feature_title">
                    {feature.title}
                  </h3>

                  <p className="Industry_class_feature_description">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Product Showcase */}
        <section className="Industry_class_showcase_section">
          <div className="Industry_class_container">
            <h2 className="Industry_class_section_title Industry_class_center">
              Built for the pace of the shop floor
            </h2>
            <p className="Industry_class_showcase_subtitle">
              Real screens from the product — task boards, calendar views,
              and shift notes your plant team can rely on.
            </p>

            <div className="Industry_class_showcase_list">
              {showcase.map((item, index) => (
                <div
                  key={index}
                  className={`Industry_class_showcase_row ${
                    index % 2 === 1 ? "Industry_class_showcase_row_reverse" : ""
                  }`}
                >
                  <div className="Industry_class_showcase_text">
                    <span className="Industry_class_showcase_eyebrow">
                      {item.eyebrow}
                    </span>
                    <h3 className="Industry_class_showcase_title">
                      {item.title}
                    </h3>
                    <p className="Industry_class_showcase_description">
                      {item.description}
                    </p>
                  </div>

                  <div className="Industry_class_showcase_media">
                    <div className="Industry_class_showcase_frame_outer">
                      <div className="Industry_class_showcase_frame_inner">
                        <ShowcaseImageLightbox
                          src={item.image}
                          alt={item.alt}
                          className="Industry_class_showcase_image"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="Industry_class_benefits_section">
          <div className="Industry_class_container">
            <div className="Industry_class_benefits_wrapper">
              <h2 className="Industry_class_section_title Industry_class_center">
                Why manufacturing teams choose Simplifyte
              </h2>

              <div className="Industry_class_benefits_list">
                {benefits.map((benefit, index) => (
                  <div key={index} className="Industry_class_benefit_item">
                    <CheckCircle2
                      size={24}
                      className="Industry_class_check_icon"
                    />

                    <p>{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="Industry_class_cta_section">
          <div className="Industry_class_container">
            <div className="Industry_class_cta_box">
              <h2 className="Industry_class_cta_title">
                Ready to simplify your floor?
              </h2>

              <p className="Industry_class_cta_text">
                Join manufacturing teams who've brought clarity to production,
                inventory, and suppliers.
              </p>

              <button
                className="Industry_class_cta_button"
                onClick={() =>
                  (window.location.href = "https://app.simplifyte.com")
                }
              >
                Start Free Trial
              </button>
            </div>
          </div>
        </section>
      </div>
      <CustomFooter />
    </>
  );
};

export default ManufacturingPage;
